import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Modal from '../UI/Modal';
import Button from '../UI/Button';
import { MagnifyingGlassIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import creditService, { UserSearchResult } from '../../api/services/credit.service';

interface AddCreditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddCreditModal: React.FC<AddCreditModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);
  const [amount, setAmount] = useState<number | ''>('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setDebouncedSearch('');
      setSelectedUser(null);
      setAmount('');
      setNote('');
      setError(null);
    }
  }, [isOpen]);

  // Debounce
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data: results, isFetching } = useQuery({
    queryKey: ['credit-user-search', debouncedSearch],
    queryFn: () => creditService.searchUsers(debouncedSearch),
    enabled: isOpen && !!debouncedSearch && !selectedUser,
    staleTime: 30 * 1000,
  });

  const handleSubmit = async () => {
    setError(null);

    if (!selectedUser) return setError('Please select a user');
    if (amount === '' || Number(amount) <= 0)
      return setError('Amount must be greater than 0');

    setSaving(true);
    try {
      await creditService.create({
        userId: selectedUser.id,
        amount: Number(amount),
        note: note.trim() || undefined,
      });
      onSuccess();
      onClose();
    } catch (e: any) {
      setError(
        e?.response?.data?.message || e?.message || 'Failed to add credit'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Admin Credit" size="md">
      <div className="space-y-4">
        {error && (
          <div className="text-xs bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        {/* User selection */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            User
          </label>

          {selectedUser ? (
            <div className="flex items-center justify-between bg-primary-50 border border-primary-200 rounded-lg px-3 py-2">
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {selectedUser.fullName}
                </p>
                <p className="text-[10px] text-gray-500">
                  @{selectedUser.username || '—'} · {selectedUser.telegramId} ·
                  Bal: {selectedUser.balance.toLocaleString()}
                </p>
              </div>
              <button
                onClick={() => setSelectedUser(null)}
                className="text-xs text-primary-700 hover:underline"
              >
                Change
              </button>
            </div>
          ) : (
            <>
              <div className="relative">
                <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name, username, telegram id..."
                  className="w-full text-sm border border-gray-200 rounded-lg pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
                />
              </div>

              {debouncedSearch && (
                <div className="mt-2 max-h-48 overflow-y-auto border border-gray-100 rounded-lg divide-y divide-gray-50">
                  {isFetching && (
                    <div className="p-3 text-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-500 border-t-transparent mx-auto" />
                    </div>
                  )}
                  {!isFetching && (results?.length || 0) === 0 && (
                    <p className="p-3 text-xs text-gray-500 text-center">
                      No users found
                    </p>
                  )}
                  {!isFetching &&
                    results?.map((u) => (
                      <button
                        key={u.id}
                        onClick={() => setSelectedUser(u)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors"
                      >
                        <p className="text-sm font-medium text-gray-900">
                          {u.fullName}
                        </p>
                        <p className="text-[10px] text-gray-500">
                          @{u.username || '—'} · {u.telegramId} · Bal:{' '}
                          {u.balance.toLocaleString()}
                        </p>
                      </button>
                    ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Amount */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Amount (ETB)
          </label>
          <input
            type="number"
            min={1}
            value={amount}
            onChange={(e) =>
              setAmount(e.target.value === '' ? '' : Number(e.target.value))
            }
            placeholder="0"
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
          {selectedUser && amount !== '' && Number(amount) > 0 && (
            <p className="text-[10px] text-gray-500 mt-1">
              New balance will be:{' '}
              <span className="font-medium text-gray-900">
                ETB {(selectedUser.balance + Number(amount)).toLocaleString()}
              </span>
            </p>
          )}
        </div>

        {/* Note */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Note (optional)
          </label>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Bonus credit, refund, etc."
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100">
          <Button
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button size="sm" onClick={handleSubmit} disabled={saving}>
            {saving ? (
              'Adding...'
            ) : (
              <>
                <CheckCircleIcon className="w-4 h-4 mr-1.5" />
                Add Credit
              </>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AddCreditModal;